// src/lib/api.ts
// Ports dash.js's getToken()/apiFetch() pattern exactly: server.js's
// doubleCsrfProtection middleware requires every mutating request
// (POST/PUT/PATCH/DELETE) to carry a valid X-CSRF-Token header,
// fetched once from GET /api/csrf-token and cached for the session.
// Skipping this silently 403s every write in production — not
// optional, not something a generic fetch wrapper would do by
// default.

let cachedCsrfToken: string | null = null;

async function getCsrfToken(): Promise<string> {
  if (cachedCsrfToken) return cachedCsrfToken;
  const res = await fetch('/api/csrf-token', { credentials: 'include' });
  if (!res.ok) throw new ApiError('Could not get a CSRF token.', res.status);
  const data = await res.json();
  cachedCsrfToken = data.csrfToken;
  return cachedCsrfToken!;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Fetch wrapper for every dashboard API call. Automatically attaches
 * the CSRF token on mutating requests, always sends the session
 * cookie, and throws ApiError (with the real status code) on any
 * non-2xx response rather than requiring every caller to check
 * res.ok manually.
 */
export async function apiFetch<T = unknown>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  if (MUTATING_METHODS.has(method)) {
    headers['X-CSRF-Token'] = await getCsrfToken();
  }

  const res = await fetch(url, {
    ...options,
    method,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    let message = `Request failed (${res.status}).`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      // response wasn't JSON — keep the generic message
    }
    throw new ApiError(message, res.status);
  }

  // 204 No Content — nothing to parse
  if (res.status === 204) return undefined as T;

  return res.json();
}
